/**
 * Sleep Aurora Curtains Visualizer - Perspective aurora sheets
 *
 * This version is built around horizon-anchored curtain sheets instead of
 * free-floating spline columns. Each sheet is rendered as many irregular
 * vertical streaks that rise from a relatively sharp base edge and diffuse
 * toward the top, which better matches real aurora perspective.
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';
import { mapFrequencyData, smoothAmplitudes } from '../lib/audio-utils.js';

export const metadata = {
  name: 'sleep-aurora-curtains',
  displayName: 'Sleep Aurora Curtains',
  contextType: '2d',
};

const MAX_STARS = 120;
const MAX_SHIMMER = 80;

class SleepAuroraCurtains extends Base2DVisualizer {
  static name = 'sleep-aurora-curtains';
  static displayName = 'Sleep Aurora Curtains';

  static getConfigUI() {
    return [
      { type: 'range', id: 'curtainCount', label: 'Curtain Count', min: 3, max: 8, default: 5 },
      { type: 'range', id: 'speed', label: 'Flow Speed', min: 1, max: 10, default: 4, scale: 0.1 },
      { type: 'range', id: 'waveStrength', label: 'Wave Strength', min: 1, max: 20, default: 12, scale: 0.01 },
      { type: 'range', id: 'intensity', label: 'Intensity', min: 20, max: 100, default: 70, scale: 0.01 },
      { type: 'range', id: 'saturation', label: 'Saturation', min: 30, max: 100, default: 78, scale: 0.01 },
      { type: 'range', id: 'trailAlpha', label: 'Trail Fade', min: 2, max: 15, default: 5, scale: 0.01 },
      { type: 'range', id: 'curtainWidth', label: 'Curtain Width', min: 3, max: 20, default: 11, scale: 0.01 },
      { type: 'range', id: 'vignetteStrength', label: 'Vignette', min: 0, max: 100, default: 32, scale: 0.01 },
      { type: 'range', id: 'starBrightness', label: 'Stars', min: 0, max: 100, default: 45, scale: 0.01 },
      { type: 'range', id: 'smoothing', label: 'Audio Smoothing', min: 50, max: 95, default: 80, scale: 0.01 },
    ];
  }

  getDefaultConfig() {
    return {};
  }

  getElementCount() {
    return 64;
  }

  init() {
    this.time = 0;
    this.isFirstFrame = true;
    this._lastFrameTime = 0;

    this._trailCanvas = null;
    this._trailCtx = null;

    this._vignetteGradient = null;
    this._vignetteW = 0;
    this._vignetteH = 0;

    this._bassEnv = 0;
    this._midEnv = 0;
    this._highEnv = 0;
    this._prevOverall = 0;
    this._transient = 0;
    this._windPhase = Math.random() * Math.PI * 2;
    this._beatFlash = 0;
    this._fallbackBeatCooldown = 0;

    this._initCurtains();
    this._initStars();
    this._initShimmerPool();
  }

  onConfigChange(changed) {
    if (changed.curtainCount !== undefined || changed.curtainWidth !== undefined) {
      this._initCurtains();
    }
    if (changed.vignetteStrength !== undefined) {
      this._vignetteGradient = null;
    }
  }

  _initCurtains() {
    const count = this.config.curtainCount;
    this.curtains = new Array(count);

    for (let i = 0; i < count; i++) {
      const t = count > 1 ? i / (count - 1) : 0.5;
      const depth = 0.15 + Math.random() * 0.85; // Larger depth = farther away

      let hue;
      if (i === count - 1 || (count > 4 && i === count - 2)) {
        hue = 270 + Math.random() * 40;
      } else {
        hue = 125 + Math.random() * 45;
      }

      const span = (0.28 + Math.random() * 0.3) * (1 - depth * 0.32);
      const streakCount = Math.round(55 + span * 160);

      this.curtains[i] = {
        depth,
        centerX: 0.12 + t * 0.76 + (Math.random() - 0.5) * 0.08,
        span,
        horizonY: 0.7 + Math.random() * 0.14 - depth * 0.08,
        baseArcAmp: 0.01 + Math.random() * 0.035,
        height: 0.28 + Math.random() * 0.28,
        perspectivePull: 0.22 + depth * 0.28,
        tilt: (Math.random() - 0.5) * 0.16,
        hue,
        hueTop: 245 + Math.random() * 45,
        phase: Math.random() * Math.PI * 2,
        waveFreq: 1.1 + Math.random() * 1.7,
        speed: 0.75 + Math.random() * 0.7,
        alphaBase: 0.12 + Math.random() * 0.1,
        vanishBias: (Math.random() - 0.5) * 0.9,
        energy: 0,
        streakCount,
        streaks: new Float32Array(streakCount * 4), // baseX, baseY, topX, topY
      };
    }

    this.curtains.sort((a, b) => b.depth - a.depth);
  }

  _initStars() {
    this.stars = new Array(MAX_STARS);
    for (let i = 0; i < MAX_STARS; i++) {
      this.stars[i] = {
        x: Math.random(),
        y: Math.random() * 0.76,
        size: 0.5 + Math.random() * 1.25,
        baseAlpha: 0.3 + Math.random() * 0.7,
        twinklePhase: Math.random() * Math.PI * 2,
        twinkleSpeed: 0.4 + Math.random() * 1.6,
      };
    }
  }

  _initShimmerPool() {
    this.shimmerPool = new Array(MAX_SHIMMER);
    for (let i = 0; i < MAX_SHIMMER; i++) {
      this.shimmerPool[i] = { x: 0, y: 0, life: 0, maxLife: 0, size: 0, alpha: 0, active: false };
    }
    this._nextShimmer = 0;
  }

  _ensureTrailCanvas() {
    const { canvas } = this;
    if (!this._trailCanvas || this._trailCanvas.width !== canvas.width || this._trailCanvas.height !== canvas.height) {
      if (typeof OffscreenCanvas !== 'undefined') {
        this._trailCanvas = new OffscreenCanvas(canvas.width, canvas.height);
      } else {
        this._trailCanvas = document.createElement('canvas');
        this._trailCanvas.width = canvas.width;
        this._trailCanvas.height = canvas.height;
      }
      this._trailCtx = this._trailCanvas.getContext('2d');
    }
  }

  draw(frequencyData, features = null) {
    const elementCount = this.getElementCount();
    if (!this._freqBuffer || this._freqBuffer.length !== elementCount) {
      this._freqBuffer = new Array(elementCount).fill(0);
    }
    const raw = mapFrequencyData(frequencyData, elementCount, this._freqBuffer);

    if (this.previousAmplitudes.length !== elementCount) {
      this.previousAmplitudes = new Array(elementCount).fill(0);
    }
    const amplitudes = smoothAmplitudes(raw, this.previousAmplitudes, this.config.smoothing);
    this._currentFeatures = features;

    const { ctx, canvas } = this;
    if (this.isFirstFrame) {
      this.clearCanvas();
      this.isFirstFrame = false;
    } else {
      this._ensureTrailCanvas();
      this._trailCtx.clearRect(0, 0, canvas.width, canvas.height);
      this._trailCtx.drawImage(canvas, 0, 0);
      this.clearCanvas();
      ctx.save();
      ctx.globalAlpha = 1 - this.config.trailAlpha;
      ctx.drawImage(this._trailCanvas, 0, 0);
      ctx.restore();
    }

    this.renderFrame(amplitudes, features);
  }

  renderFrame(amplitudes) {
    const now = performance.now();
    const dt = this._lastFrameTime > 0 ? Math.min((now - this._lastFrameTime) / 1000, 0.05) : 1 / 60;
    this._lastFrameTime = now;
    this.time += dt;

    const third = Math.floor(amplitudes.length / 3);
    let bassSum = 0;
    let midSum = 0;
    let highSum = 0;
    for (let i = 0; i < third; i++) bassSum += amplitudes[i];
    for (let i = third; i < third * 2; i++) midSum += amplitudes[i];
    for (let i = third * 2; i < amplitudes.length; i++) highSum += amplitudes[i];

    const bassEnergy = bassSum / third;
    const midEnergy = midSum / third;
    const highEnergy = highSum / (amplitudes.length - third * 2);
    const overallEnergy = (bassEnergy + midEnergy + highEnergy) / 3;

    this._bassEnv += (bassEnergy - this._bassEnv) * 0.22;
    this._midEnv += (midEnergy - this._midEnv) * 0.2;
    this._highEnv += (highEnergy - this._highEnv) * 0.27;

    const transient = Math.max(0, overallEnergy - this._prevOverall * 0.9);
    this._prevOverall = overallEnergy;
    this._transient = Math.max(this._transient * 0.9, transient * 2.1);

    const beat = this.beatDetected || this._detectFallbackBeat(this._bassEnv, overallEnergy);
    if (beat) {
      this._beatFlash = Math.min(1, this._beatFlash + 0.42 + this._transient);
    }
    this._beatFlash *= 0.9;
    this._windPhase += dt * (0.2 + overallEnergy * 1.2 + this._transient * 0.7);

    this._drawSkyGlow(overallEnergy, highEnergy);
    if (this.config.starBrightness > 0) {
      this._drawStars(highEnergy, overallEnergy);
    }

    for (let i = 0; i < this.curtains.length; i++) {
      this._drawCurtainSheet(this.curtains[i], this._bassEnv, this._midEnv, this._highEnv, this._transient);
    }

    this._drawShimmer(dt, highEnergy + this._transient * 0.4);

    if (this.config.vignetteStrength > 0) {
      this._drawVignette();
    }
    if (this._beatFlash > 0.03) {
      this._drawBeatVeil(this._beatFlash);
    }
  }

  _detectFallbackBeat(bassEnergy, overallEnergy) {
    if (this._fallbackBeatCooldown > 0) {
      this._fallbackBeatCooldown--;
      return false;
    }
    const isBeat = bassEnergy > 0.33 && bassEnergy > this._bassEnv * 1.12 && overallEnergy > 0.19;
    if (isBeat) this._fallbackBeatCooldown = 7;
    return isBeat;
  }

  _drawSkyGlow(overallEnergy, highEnergy) {
    const { ctx, canvas } = this;
    const w = canvas.width;
    const h = canvas.height;

    const alpha = 0.06 + overallEnergy * 0.16 + highEnergy * 0.05;
    const shift = Math.sin(this._windPhase * 0.55) * w * 0.1;
    const grad = ctx.createRadialGradient(
      w * 0.5 + shift,
      h * 0.14,
      h * 0.04,
      w * 0.5 + shift,
      h * 0.22,
      h * 0.95
    );
    grad.addColorStop(0, `rgba(92, 250, 210, ${alpha * 0.45})`);
    grad.addColorStop(0.28, `rgba(120, 165, 255, ${alpha * 0.24})`);
    grad.addColorStop(0.66, `rgba(35, 24, 55, ${alpha * 0.06})`);
    grad.addColorStop(1, 'rgba(0, 0, 0, 0)');

    ctx.save();
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }

  _drawCurtainSheet(curtain, bassEnergy, midEnergy, highEnergy, transient) {
    const { ctx, canvas, config } = this;
    const w = canvas.width;
    const h = canvas.height;

    const sat = Math.round(config.saturation * 100);
    const hue = curtain.hue;
    const hueTop = curtain.hueTop;
    const flow = config.speed * (0.7 + bassEnergy * 1.1 + transient * 0.7);

    const windX = Math.sin(this._windPhase + curtain.phase * 0.7) * w * 0.06;
    const vanishX = w * (
      0.5
      + Math.sin(this._windPhase * 0.35 + curtain.vanishBias * 2) * 0.07
      + curtain.vanishBias * 0.05
    );

    const streaks = curtain.streaks;
    const streakCount = curtain.streakCount;
    curtain.energy += ((0.5 * bassEnergy + 0.35 * midEnergy + 0.15 * highEnergy) - curtain.energy) * 0.16;

    for (let i = 0; i < streakCount; i++) {
      const u = streakCount > 1 ? i / (streakCount - 1) : 0.5;
      const edge = Math.abs(u - 0.5) * 2;
      const idx = i * 4;

      const xNorm = curtain.centerX + (u - 0.5) * curtain.span * config.curtainWidth;
      const wave = Math.sin(
        u * Math.PI * 2 * curtain.waveFreq + curtain.phase + this.time * flow * curtain.speed
      );
      const micro = Math.sin(
        u * 31 + curtain.phase * 1.7 - this.time * flow * 1.2
      ) * 0.45;
      const arc = Math.sin((u - 0.5) * Math.PI) * curtain.baseArcAmp;
      const horizonJitter = (wave * 0.45 + micro * 0.2) * 0.02 * config.waveStrength;

      const baseX = xNorm * w + windX * (0.65 + 0.35 * (1 - edge));
      const baseY = (
        curtain.horizonY
        + arc
        + horizonJitter * (0.45 + midEnergy * 0.75)
        + (u - 0.5) * curtain.tilt * 0.08
      ) * h;

      const columnHeight = curtain.height
        * (0.62 + 0.45 * (1 - Math.pow(edge, 1.25)))
        * (0.8 + curtain.energy * 1.4 + transient * 0.8);

      const flameNoise = Math.sin(u * 17 + curtain.phase * 2.3 + this.time * flow * 1.25)
        + 0.6 * Math.sin(u * 43 - curtain.phase * 0.9 - this.time * flow * 1.8);
      const topLift = columnHeight * (0.82 + flameNoise * (0.08 + highEnergy * 0.14));
      const topY = baseY - topLift * h;

      const perspective = curtain.perspectivePull * (0.5 + 0.5 * (1 - edge));
      const flameBend = Math.sin(u * 23 + this.time * flow * 1.4 + curtain.phase) * w * (0.003 + highEnergy * 0.006);
      const topX = baseX + (vanishX - baseX) * perspective + flameBend;

      streaks[idx] = baseX;
      streaks[idx + 1] = baseY;
      streaks[idx + 2] = topX;
      streaks[idx + 3] = topY;
    }

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    for (let i = 0; i < streakCount; i++) {
      const u = streakCount > 1 ? i / (streakCount - 1) : 0.5;
      const edge = Math.abs(u - 0.5) * 2;
      const idx = i * 4;
      const baseX = streaks[idx];
      const baseY = streaks[idx + 1];
      const topX = streaks[idx + 2];
      const topY = streaks[idx + 3];

      const energyPulse = 0.45 + curtain.energy * 1.25 + transient * 0.9;
      const alpha = curtain.alphaBase * config.intensity * energyPulse * (1 - edge * 0.7);
      if (alpha < 0.008 || topY >= baseY) continue;

      const width = (0.9 + (1 - edge) * 1.2) * (1.05 - curtain.depth * 0.35) * (0.8 + highEnergy * 0.9);

      const grad = ctx.createLinearGradient(baseX, baseY, topX, topY);
      grad.addColorStop(0, `hsla(${hue}, ${sat}%, 70%, ${alpha})`);
      grad.addColorStop(0.12, `hsla(${hue}, ${sat}%, 66%, ${alpha * 0.86})`);
      grad.addColorStop(0.5, `hsla(${(hue + hueTop) * 0.5}, ${sat}%, 58%, ${alpha * 0.36})`);
      grad.addColorStop(1, `hsla(${hueTop}, ${sat}%, 54%, 0)`);

      const cx = (baseX + topX) * 0.5 + Math.sin(this.time * flow + u * 9 + curtain.phase) * w * 0.012 * (1 - edge);
      const cy = baseY * 0.58 + topY * 0.42;

      ctx.strokeStyle = grad;
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(baseX, baseY);
      ctx.quadraticCurveTo(cx, cy, topX, topY);
      ctx.stroke();
    }

    ctx.restore();
    this._drawCurtainBaseEdge(curtain, bassEnergy, transient);
  }

  _drawCurtainBaseEdge(curtain, bassEnergy, transient) {
    const { ctx, config } = this;
    const sat = Math.round(config.saturation * 100);
    const hue = curtain.hue;
    const streaks = curtain.streaks;
    const streakCount = curtain.streakCount;

    const alpha = config.intensity * (0.24 + bassEnergy * 0.55 + transient * 0.42) * (0.9 - curtain.depth * 0.3);
    if (alpha < 0.02) return;

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.strokeStyle = `hsla(${hue}, ${sat}%, 80%, ${Math.min(alpha, 0.85)})`;
    ctx.lineWidth = 1.6 + bassEnergy * 2.1;
    ctx.beginPath();

    for (let i = 0; i < streakCount; i++) {
      const idx = i * 4;
      const x = streaks[idx];
      const y = streaks[idx + 1];
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.restore();
  }

  _drawStars(highEnergy, overallEnergy) {
    const { ctx, canvas, config } = this;
    const w = canvas.width;
    const h = canvas.height;

    ctx.save();
    ctx.fillStyle = '#ffffff';
    for (let i = 0; i < MAX_STARS; i++) {
      const s = this.stars[i];
      const drift = Math.sin(this._windPhase * 0.5 + i * 0.27) * 0.0017;
      const x = ((s.x + drift + 1) % 1) * w;
      const twinkle = 0.5 + 0.5 * Math.sin(s.twinklePhase + this.time * s.twinkleSpeed + highEnergy * 4.8);
      const alpha = s.baseAlpha * twinkle * config.starBrightness * (0.012 + overallEnergy * 0.01);
      if (alpha < 0.001) continue;

      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(x, s.y * h, s.size * (0.8 + highEnergy * 0.6), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  _drawShimmer(dt, highEnergy) {
    const { ctx, canvas } = this;
    const spawnCount = Math.min(Math.floor(highEnergy * 6), 6);

    for (let s = 0; s < spawnCount; s++) {
      const p = this.shimmerPool[this._nextShimmer];
      this._nextShimmer = (this._nextShimmer + 1) % MAX_SHIMMER;

      const curtain = this.curtains[Math.floor(Math.random() * this.curtains.length)];
      const streakCount = curtain.streakCount;
      const sample = Math.floor(Math.random() * streakCount);
      const idx = sample * 4;
      const topX = curtain.streaks[idx + 2];
      const topY = curtain.streaks[idx + 3];

      p.x = topX + (Math.random() - 0.5) * canvas.width * 0.015;
      p.y = topY + (Math.random() - 0.5) * canvas.height * 0.03;
      p.life = 1;
      p.maxLife = 0.7 + Math.random() * 1.4;
      p.size = 0.7 + Math.random() * 2.1;
      p.alpha = 0.25 + Math.random() * 0.45;
      p.active = true;
    }

    ctx.save();
    ctx.fillStyle = '#ffffff';
    for (let i = 0; i < MAX_SHIMMER; i++) {
      const p = this.shimmerPool[i];
      if (!p.active) continue;

      p.life -= dt / p.maxLife;
      if (p.life <= 0) {
        p.active = false;
        continue;
      }

      p.y -= (8 + highEnergy * 22) * dt;
      const lifeAlpha = p.life * p.life;
      ctx.globalAlpha = p.alpha * lifeAlpha * (0.2 + highEnergy * 0.52);
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * lifeAlpha, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  _drawBeatVeil(strength) {
    const { ctx, canvas } = this;
    const alpha = Math.min(0.2, strength * 0.16);
    if (alpha <= 0.005) return;

    const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
    grad.addColorStop(0, `rgba(186, 255, 235, ${alpha})`);
    grad.addColorStop(0.36, `rgba(145, 222, 255, ${alpha * 0.45})`);
    grad.addColorStop(1, 'rgba(0, 0, 0, 0)');

    ctx.save();
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
  }

  _drawVignette() {
    const { ctx, canvas, config } = this;
    if (this._vignetteW !== canvas.width || this._vignetteH !== canvas.height || !this._vignetteGradient) {
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      const radius = Math.max(canvas.width, canvas.height) * 0.7;
      this._vignetteGradient = ctx.createRadialGradient(cx, cy, radius * 0.3, cx, cy, radius);
      this._vignetteGradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
      this._vignetteGradient.addColorStop(1, 'rgba(0, 0, 0, 1)');
      this._vignetteW = canvas.width;
      this._vignetteH = canvas.height;
    }

    ctx.save();
    ctx.globalAlpha = config.vignetteStrength;
    ctx.fillStyle = this._vignetteGradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
  }

  clear() {
    super.clear();
    this.time = 0;
    this.isFirstFrame = true;
    this._lastFrameTime = 0;
    this._bassEnv = 0;
    this._midEnv = 0;
    this._highEnv = 0;
    this._prevOverall = 0;
    this._transient = 0;
    this._beatFlash = 0;
    this._fallbackBeatCooldown = 0;

    for (let i = 0; i < MAX_SHIMMER; i++) {
      this.shimmerPool[i].active = false;
    }
    this._nextShimmer = 0;
    this._initCurtains();

    if (this._trailCtx && this._trailCanvas) {
      this._trailCtx.clearRect(0, 0, this._trailCanvas.width, this._trailCanvas.height);
    }
    this._vignetteGradient = null;
  }
}

VisualizerRegistry.register(SleepAuroraCurtains);

export { SleepAuroraCurtains };
export default SleepAuroraCurtains;
